/**
 * Shared reward types (Plan 02-01 Task 2 / D-11).
 *
 * `REWARD_CATEGORIES` is the fixed v1 enum used by:
 *  - apps/api  → schema.rewards.category check + service-layer Zod
 *  - apps/web  → reward-create form chip set (admin scope)
 *
 * Adding a category is a v2 change so the form chips stay stable in v1
 * (D-11 specifics — "Reward category enum is fixed in v1").
 *
 * Norwegian Bokmål values (no English aliases): `tid` (time), `opplevelse`
 * (experience), `mat` (food), `annet` (other). The `mat` category is the
 * only one that triggers the allergen filter in `rewards.service`.
 */
import { z } from 'zod';

export const REWARD_CATEGORIES = ['tid', 'opplevelse', 'mat', 'annet'] as const;
export type RewardCategory = (typeof REWARD_CATEGORIES)[number];

// ─── Allergirapport for familieadmin (#219) ─────────────────────────────────

/**
 * Ett treff som skjuler en belønning for et medlem: allergen-id-en (brikke
 * som `dairy`, eller en egendefinert allergi som den er) og nøkkelordet som
 * slo til. Samme form som `AllergenHit` fra utkastskanningen.
 */
export const RewardAllergyHitSchema = z.object({
  allergen: z.string(),
  keyword: z.string(),
});

/**
 * En aktiv belønning som lag 2 skjuler for minst ett medlem, og for hvem.
 * Bare familiens egne belønninger og medlemmer (Pattern 9), og bare til
 * familieadmin — systemadmin ser ikke innhold (D3).
 */
export const AllergyHiddenRewardSchema = z.object({
  reward_id: z.string(),
  title: z.string(),
  hidden_for: z
    .array(
      z.object({
        member_id: z.string(),
        short_name: z.string(),
        hits: z.array(RewardAllergyHitSchema).min(1),
      }),
    )
    .min(1),
});
export type AllergyHiddenReward = z.infer<typeof AllergyHiddenRewardSchema>;

/** Svaret fra `GET /api/rewards/admin/hidden-by-allergy`. */
export const AllergyHiddenRewardsResponseSchema = z.object({
  rewards: z.array(AllergyHiddenRewardSchema),
});
export type AllergyHiddenRewardsResponse = z.infer<typeof AllergyHiddenRewardsResponseSchema>;
